import { ChevronDown, ChevronRight, FileText } from 'lucide-react';
import { IChecklistSection, IChecklistResponse, IChecklistItem } from '@/types/checklist.types';
import { ICompanyData } from '@/types/company-data.types';
import useAuthState from '@/states/auth.state';
import { getDeadlineDateFromWorkDays } from '@/pages/company-profile/checklist/implementationDay';

const getHref = (ref: string) => {
  const trimmed = ref.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }
  if (trimmed.startsWith('www.')) {
    return `https://${trimmed}`;
  }
  if (trimmed.includes('.') && !trimmed.includes(' ')) {
    return `https://${trimmed}`;
  }
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
};

const parseReference = (ref: string): { label: string; href: string } => {
  const trimmed = ref.trim();
  if (trimmed.includes('|')) {
    const parts = trimmed.split('|');
    const label = parts[0].trim();
    const rawLink = parts.slice(1).join('|').trim();
    return {
      label: label || rawLink,
      href: getHref(rawLink || label),
    };
  }
  return {
    label: trimmed,
    href: getHref(trimmed),
  };
};

const STATIC_REFERENSI_MAP: Record<string, string[]> = {
  'Mengunduh Dokumen AOS seluruh Jabatan di menu Download Dokumen AOS': ['1. Download dokumen AOS.png'],
  'Mengunggah dan menyimpan softcopy Dokumen AOS seluruh Jabatan ke Google Drive milik perusahaan': [
    '2. Google Drive Perusahaan.png',
  ],
  'Mencetak Dokumen AOS seluruh Jabatan dalam bentuk hardcopy': ['3. Cetak dokumen AOS.png'],
  'Memasukkan hardcopy Dokumen AOS dalam business file per Jabatan. Disarankan business file yang digunakan memiliki warna sesuai dengan warna Brand Identity perusahaan':
    ['4. Business file dokumen AOS.png'],
  'Menempatkan Dokumen AOS seluruh Jabatan di rak arsip milik Bagian SDM': [
    '5. Memasukkan business file ke lemari arsip.png',
  ],
  'Menerbitkan Surat Keputusan (SK) Tim Implementasi AOS': ['6. SK Penunjukan Penanggungjawab Implementasi AOS.pdf'],
  'Mewajibkan Tim Implementasi AOS melihat video cara menggunakan Dokumen AOS': [
    '7. Video Cara Menggunakan Dokumen AOS.mp4',
  ],
  'Melakukan sosialisasi Implementasi AOS kepada seluruh karyawan oleh Tim Implementasi AOS': [
    '8. Sosialisasi IMplementasi AOS.png',
  ],
  'Melakukan penandatanganan Pakta Integritas oleh seluruh karyawan perusahaan (kickoff program Implementasi AOS)': [
    '9. Pakta Integritas Implementasi AOS.pdf',
  ],
  'Membuat desain Poster Purpose, Vision, Mission, Culture, dan Struktur Organisasi Perusahaan': [
    'Poster Purpose, Vision, Mission, Culture, Structure Organization.pdf',
  ],
  'Mencetak dan membingkai Poster Purpose, Vision, Mission, Culture, dan Struktur Organisasi Perusahaan': [
    'Poster Purpose, Vision, Mission, Culture, Structure Organization.pdf',
  ],
  'Memasang Poster Purpose, Vision, Mission, Culture, dan Struktur Organisasi Perusahaan di tempat strategis perusahaan':
    ['Poster Purpose, Vision, Mission, Culture, Structure Organization.pdf'],
  'Membaca Purpose, Vision, Mission, dan Culture Perusahaan bersama seluruh tim pada saat briefing pagi': [
    '13. Membaca Purpose, Vision, Mission, dan Culture Perusahaan bersama seluruh tim pada saat briefing pagi.png',
  ],
  'Menyusun jadwal pelatihan Dokumen AOS setiap Jabatan terkait': ['14. Jadwal Training SOP.pdf'],
  'Mencetak salinan Dokumen AOS setiap Jabatan (fotocopy) untuk proses pelatihan': ['15. Fotokopian dokumen AOS.png'],
  'Memberikan salinan Dokumen AOS kepada setiap Jabatan': ['16. penyerahan fotokopian dokumen AOS.png'],
  'Melaksanakan pelatihan Dokumen AOS yang dilakukan oleh atasan setiap Jabatan': ['17. Pelatihan Dokumen AOS.png'],
  'Membuat desain Business Goal (AOS #4)': ['18. Poster target omset.png'],
  'Mencetak dan membingkai poster Business Goals': ['19. Mencetak Membingkai Memasang Business Goals.png'],
  'Memasang poster Business Goal di Ruang Kerja Pimpinan Perusahaan': [
    '19. Mencetak Membingkai Memasang Business Goals.png',
  ],
  'Menyusun Tata Tertib Perusahaan and melakukan sosialisasi': ['Contoh Tata Tertib Perusahaan.pdf'],
  'Menyusun Tata Tertib Perusahaan dan melakukan sosialisasi': ['Contoh Tata Tertib Perusahaan.pdf'],
  'Menyusun Tabel Ketentuan Kehadiran, Izin, dan Cuti and melakukan sosialisasi': [
    'Contoh Tabel Ketentuan Kehadiran.pdf',
  ],
  'Menyusun Tabel Ketentuan Kehadiran, Izin, dan Cuti dan melakukan sosialisasi': [
    'Contoh Tabel Ketentuan Kehadiran.pdf',
  ],
  'Menyusun dan menandatangani Addendum Kontrak Kerja setiap karyawan': ['Kontrak Kerja Staf Masak Bakso Fedas.pdf'],
  'Menerapkan atribut identitas karyawan sesuai dengan standar perusahaan (Lanyard, ID Card, Seragam)': [
    'ID Card Karyawan Bakso Fedas.png',
    'Poster Grooming Karyawan Bakso Fedas.png',
  ],
};

interface ChecklistSectionProps {
  section: IChecklistSection;
  isExpanded: boolean;
  completedCount: number;
  totalItems: number;
  responses: IChecklistResponse[];
  companyData?: ICompanyData;
  readOnlyExceptStatus?: boolean;
  isFullReadOnly?: boolean;
  implementationStartedAt?: string | null;
  onToggle: () => void;
  onUpdateResponse: (responseId: number, field: string, value: string) => void;
  onOpenMedia: (responseId: number) => void;
  getMediaCount: (responseId: number) => number;
}

export const ChecklistSection = ({
  section,
  isExpanded,
  completedCount,
  totalItems,
  responses,
  companyData: _companyData,
  readOnlyExceptStatus = false,
  isFullReadOnly = false,
  implementationStartedAt = null,
  onToggle,
  onUpdateResponse,
  onOpenMedia,
  getMediaCount,
}: ChecklistSectionProps) => {
  const getResponseForItem = (itemId: number): IChecklistResponse | undefined => {
    return responses.find(r => r.item_id === itemId);
  };

  const subSections = section.sub_sections || [];

  // Build each sub-section's item group up front so numbering can continue
  // sequentially across the whole section instead of resetting per sub-section.
  let runningIndex = 0;
  const subSectionGroups = subSections
    .map((subSection, subIndex) => {
      const items = subSection.items || [];
      const startIndex = runningIndex;
      runningIndex += items.length;
      return {
        subSection,
        label: subSection.code || `${section.code}.${subIndex + 1}`,
        items,
        startIndex,
      };
    })
    .filter(group => group.items.length > 0);

  const hasItems = subSectionGroups.length > 0;

  return (
    <div className="bg-white dark:bg-gray-900 group">
      {/* Section Header */}
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          )}
          <div className="text-left">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {section.code}. {section.name}
              </h3>
            </div>
            {section.description && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{section.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500 dark:text-gray-400 hidden md:block">
            {totalItems === 0 ? '0%' : `${Math.round((completedCount / totalItems) * 100)}%`}
          </span>
          <div className="w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
            <div
              className={`bg-blue-600 h-2 rounded-full transition-all duration-300 ${
                completedCount === 0 || totalItems === 0 ? 'w-0' : completedCount === totalItems ? 'w-full' : ''
              }`}
              {...(completedCount > 0 &&
                totalItems > 0 &&
                completedCount < totalItems && {
                  style: { width: `${(completedCount / totalItems) * 100}%` },
                })}
            />
          </div>
        </div>
      </button>

      {/* Section Content */}
      {isExpanded && hasItems && (
        <div className="border-t border-gray-200 dark:border-gray-700">
          <div className="w-full">
            {/* Sub Sections, each with its own items — numbering continues sequentially across the section */}
            {subSectionGroups.map(group => (
              <SubSectionGroup
                key={group.subSection.id}
                label={group.label}
                name={group.subSection.name}
                items={group.items}
                startIndex={group.startIndex}
                getResponseForItem={getResponseForItem}
                readOnlyExceptStatus={readOnlyExceptStatus}
                isFullReadOnly={isFullReadOnly}
                implementationStartedAt={implementationStartedAt}
                onUpdateResponse={onUpdateResponse}
                onOpenMedia={onOpenMedia}
                getMediaCount={getMediaCount}
              />
            ))}
          </div>
        </div>
      )}

      {/* Empty state when expanded but no items */}
      {isExpanded && !hasItems && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-8 text-center text-gray-500 dark:text-gray-400">
          Tidak ada item checklist pada kategori ini
        </div>
      )}
    </div>
  );
};

interface SubSectionGroupProps {
  label: string;
  name: string;
  items: IChecklistItem[];
  startIndex: number;
  getResponseForItem: (itemId: number) => IChecklistResponse | undefined;
  readOnlyExceptStatus?: boolean;
  isFullReadOnly?: boolean;
  implementationStartedAt?: string | null;
  onUpdateResponse: (responseId: number, field: string, value: string) => void;
  onOpenMedia: (responseId: number) => void;
  getMediaCount: (responseId: number) => number;
}

const SubSectionGroup = ({
  label,
  name,
  items,
  startIndex,
  getResponseForItem,
  readOnlyExceptStatus,
  isFullReadOnly,
  implementationStartedAt,
  onUpdateResponse,
  onOpenMedia,
  getMediaCount,
}: SubSectionGroupProps) => {
  return (
    <div>
      <div className="px-4 py-2 md:pl-6 bg-[#dce8f8] dark:bg-[#0a1628] border-b border-x border-gray-200 dark:border-gray-700 text-sm font-semibold tracking-wide text-[#071753] dark:text-blue-200">
        {label}. {name}
      </div>
      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {items.map((item, i) => {
          const response = getResponseForItem(item.id);
          const isCompleted = response?.status === 'done' || response?.is_completed || false;
          return (
            <ChecklistItemRow
              key={item.id}
              index={startIndex + i + 1}
              item={item}
              response={response}
              isCompleted={isCompleted}
              readOnlyExceptStatus={readOnlyExceptStatus}
              isFullReadOnly={isFullReadOnly}
              implementationStartedAt={implementationStartedAt}
              onUpdateResponse={onUpdateResponse}
              onOpenMedia={onOpenMedia}
              getMediaCount={getMediaCount}
            />
          );
        })}
      </div>
    </div>
  );
};

interface ChecklistItemRowProps {
  index: number;
  item: IChecklistItem;
  response: IChecklistResponse | undefined;
  isCompleted: boolean;
  readOnlyExceptStatus?: boolean;
  isFullReadOnly?: boolean;
  implementationStartedAt?: string | null;
  onUpdateResponse: (responseId: number, field: string, value: string) => void;
  onOpenMedia?: (responseId: number) => void;
  getMediaCount?: (responseId: number) => number;
}

const ChecklistItemRow = ({
  index,
  item,
  response,
  isCompleted,
  readOnlyExceptStatus = false,
  isFullReadOnly = false,
  implementationStartedAt = null,
  onUpdateResponse,
}: ChecklistItemRowProps) => {
  const hasDeadline = !!response?.deadline;

  const canMarkAsDone = hasDeadline;

  const implementationDay = item.estimated_work_days ?? null;
  const deadlineDate = getDeadlineDateFromWorkDays(item.estimated_work_days, implementationStartedAt);

  const { isSuperAdmin } = useAuthState();

  const itemReferences =
    item.references && item.references.length > 0 ? item.references : STATIC_REFERENSI_MAP[item.description];

  return (
    <div
      className={`md:grid md:grid-cols-[60px_minmax(200px,2fr)_140px_minmax(180px,1.2fr)_160px] md:items-center group transition-colors border-b border-x border-gray-200 dark:border-gray-700 ${
        isCompleted
          ? 'bg-green-50 dark:bg-green-900/20'
          : index % 2 === 1
          ? 'bg-white dark:bg-gray-900 hover:bg-blue-50/40 dark:hover:bg-gray-800/60'
          : 'bg-gray-50/70 dark:bg-gray-800/30 hover:bg-blue-50/40 dark:hover:bg-gray-800/60'
      }`}
    >
      {/* Mobile: Header with No & Description */}
      <div className="p-4 md:p-3 md:pl-4 flex flex-col md:block gap-2 col-span-2 md:col-span-1 md:contents">
        <div className="flex items-start gap-3 md:hidden">
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400 min-w-[24px]">{index}.</span>
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 flex-1">
            {item.description}
            {item.is_required && <span className="text-red-500 ml-1">*</span>}
          </div>
        </div>

        {/* Desktop: No */}
        <div className="hidden md:flex md:items-center text-sm text-gray-900 dark:text-gray-100 p-3 pl-6 h-full min-w-0 md:border-r md:border-gray-200 md:dark:border-gray-700">
          {index}
        </div>

        {/* Desktop: Description */}
        <div className="hidden md:flex md:items-center text-sm text-gray-900 dark:text-gray-100 p-3 h-full min-w-0 break-words md:border-r md:border-gray-200 md:dark:border-gray-700">
          <div className="w-full">
            {item.description}
            {item.is_required && <span className="text-red-500 ml-1">*</span>}
          </div>
        </div>
      </div>

      {/* Mobile: Form Controls Container */}
      <div className="px-4 pb-4 md:p-0 md:contents space-y-3 md:space-y-0">
        {/* Batas Waktu — read-only, derived from estimated_work_days + implementation start */}
        <div className="md:p-3 md:flex md:flex-col md:justify-center min-w-0 md:border-r md:border-gray-200 md:dark:border-gray-700">
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 md:hidden">
            Batas Waktu
          </label>
          <div
            aria-label="Pilih batas waktu"
            className="w-full h-10 px-2 py-1 border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-lg flex flex-col justify-center select-none"
          >
            {deadlineDate ? (
              <>
                <span className="text-sm text-orange-500 font-semibold leading-none">{deadlineDate}</span>
                {implementationDay !== null && (
                  <span className="text-[10px] text-blue-600 dark:text-blue-300 font-medium leading-none mt-1">
                    Hari {implementationDay}
                  </span>
                )}
              </>
            ) : implementationDay !== null ? (
              <span className="text-sm text-blue-600 dark:text-blue-400 font-semibold leading-none">
                Hari {implementationDay}
              </span>
            ) : (
              <span className="text-sm text-gray-400 dark:text-gray-500 italic leading-none">—</span>
            )}
          </div>
        </div>

        {/* Referensi Column */}
        <div className="md:p-3 md:flex md:flex-col md:justify-center min-w-0 md:border-r md:border-gray-200 md:dark:border-gray-700">
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 md:hidden">Referensi</label>
          {itemReferences && itemReferences.length > 0 ? (
            <ul className="space-y-1.5 py-1">
              {itemReferences.map((ref, idx) => {
                const { label, href } = parseReference(ref);
                return (
                  <li key={idx} className="flex items-center gap-1.5">
                    <FileText size={14} className="text-gray-400 dark:text-gray-500 shrink-0" />
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline break-all font-medium leading-tight"
                    >
                      {label}
                    </a>
                  </li>
                );
              })}
            </ul>
          ) : (
            <span className="text-xs text-gray-400 dark:text-gray-500 italic">—</span>
          )}
        </div>

        {/* Status */}
        <div className="md:p-3 md:flex md:items-center min-w-0">
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 md:hidden">Status</label>
          <select
            value={response?.status === 'done' ? 'done' : 'pending'}
            onChange={e => response && !isFullReadOnly && onUpdateResponse(response.id, 'status', e.target.value)}
            className="w-full h-10 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            aria-label="Pilih status"
            disabled={isFullReadOnly}
          >
            <option value="pending">Belum dikerjakan</option>
            <option value="done">Sudah dikerjakan</option>
          </select>
        </div>
      </div>
    </div>
  );
};
